import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

const apiUrl = (environment.apiUrl ?? '').replace(/\/$/, '');

/**
 * Attach the bearer token to API calls.
 *
 * Scoped to `environment.apiUrl` on purpose: a blanket interceptor would also
 * put the token on requests for local assets (i18n files, bundled data) and,
 * worse, on any third-party URL the app ever fetches. Anonymous endpoints
 * simply ignore the header, so there is no need to list which routes need it.
 *
 * A 401 on a request that carried the token means the session is over (the
 * token expired, or the signing secret was rotated): sign out and send the
 * user to the login page, rather than leaving every admin page showing a
 * "couldn't load" error while the app still thinks they are signed in.
 * `/v1/auth/*` is left alone — a 401 there is a wrong password, not a dead
 * session.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!apiUrl || !req.url.startsWith(apiUrl)) {
    return next(req);
  }
  const auth = inject(AuthService);
  const token = auth.currentToken();
  if (!token) {
    return next(req);
  }
  const userService = inject(UserService);
  const router = inject(Router);
  const isAuthCall = req.url.startsWith(`${apiUrl}/v1/auth/`);
  return next(
    req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }),
  ).pipe(
    catchError((err: unknown) => {
      // Only the first of several parallel 401s still sees this token.
      if (
        !isAuthCall &&
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        auth.currentToken() === token
      ) {
        const returnUrl = router.url;
        void Promise.all([userService.logout(), auth.logout()]).then(() =>
          router.navigateByUrl(
            router.createUrlTree(['/login'], {
              queryParams: returnUrl.startsWith('/login') ? {} : { returnUrl },
            }),
          ),
        );
      }
      return throwError(() => err);
    }),
  );
};
